/**
 * How one module section of the setup grid is broken up into topics.
 *
 * Most subdomains are steps of one process, so a section reads as a handful of
 * things an admin actually decides about. The rules name those topics; the
 * catalogue still decides which events exist, so a subdomain no rule mentions
 * becomes a topic of its own instead of disappearing.
 *
 * @link ./NotificationTopicRow for the row each topic renders.
 */
import type { SetupEvent } from './types';

/** One decision in a section, with the rows behind it. */
export interface EventTopic {
  /** Unique across sections, so open state can live in one map. */
  key: string;
  label: string;
  events: SetupEvent[];
}

interface TopicRule {
  key: string;
  label: string;
  subdomains: readonly string[];
}

/** Topics per module, in the order the section lists them. */
const TOPIC_RULES: Record<string, readonly TopicRule[]> = {
  purchasing: [
    { key: 'line_changes', label: 'Order line changes', subdomains: ['po_line', 'po_header'] },
    { key: 'schedule', label: 'Schedule changes', subdomains: ['schedule'] },
    { key: 'call_off', label: 'Call-offs', subdomains: ['call_off'] },
    { key: 'appeal', label: 'Delivery appeals', subdomains: ['appeal'] },
    {
      key: 'messages',
      label: 'Messages and queries',
      subdomains: ['message', 'query', 'asn', 'supplier_message'],
    },
    { key: 'grn', label: 'Goods received', subdomains: ['grn'] },
    { key: 'holds', label: 'Holds and critical flags', subdomains: [] },
    { key: 'otif', label: 'OTIF decisions', subdomains: ['otif'] },
    { key: 'reports', label: 'Forecasts and reports', subdomains: ['forecast', 'digest', 'import'] },
  ],
  quality: [
    { key: 'ncr', label: 'NCR progress and comments', subdomains: ['ncr', 'car'] },
    { key: 'collection', label: 'Collections', subdomains: ['collection'] },
    { key: 'credit', label: 'Credits', subdomains: ['credit'] },
    { key: 'replacement', label: 'Replacements', subdomains: ['replacement'] },
    { key: 'rejection', label: 'Rejections', subdomains: ['rejection'] },
    { key: 'concession', label: 'Concessions', subdomains: ['concession'] },
    { key: 'digest', label: 'Daily summary', subdomains: ['digest'] },
  ],
  compliance: [
    { key: 'document', label: 'Documents', subdomains: ['document'] },
    { key: 'questionnaire', label: 'Questionnaires', subdomains: ['questionnaire'] },
    { key: 'audit', label: 'Audits', subdomains: ['audit'] },
    { key: 'supplier', label: 'Supplier ratings', subdomains: ['supplier'] },
  ],
  onboarding: [
    { key: 'invitation', label: 'Invitations', subdomains: ['invitation'] },
    { key: 'workflow', label: 'Approvals', subdomains: ['workflow'] },
  ],
  rfq: [{ key: 'rfq', label: 'RFQs', subdomains: ['rfq'] }],
  system: [
    { key: 'user', label: 'Users', subdomains: ['user'] },
    { key: 'announcement', label: 'Announcements', subdomains: ['announcement'] },
    { key: 'report', label: 'Reports', subdomains: ['report'] },
  ],
};

/** The `po_line` events that are about a line's state, not a change to it. */
const HOLD_EVENTS: ReadonlySet<string> = new Set([
  'purchasing.po_line.hold_applied',
  'purchasing.po_line.hold_released',
  'purchasing.po_line.flagged_critical',
  'purchasing.po_line.critical_flag_cleared',
]);

const ruleFor = (event: SetupEvent, rules: readonly TopicRule[]): TopicRule | undefined =>
  HOLD_EVENTS.has(event.key)
    ? rules.find((rule) => rule.key === 'holds')
    : rules.find((rule) => rule.subdomains.includes(event.subdomain));

/**
 * Group rows into topics: the module's listed order first, then any subdomain
 * no rule names, in platform order. Rows keep platform order inside a topic.
 *
 * @param events - the rows a section is about to render, all from one module.
 * @returns one topic per decision, empty ones left out.
 */
export function groupByTopic(events: SetupEvent[]): EventTopic[] {
  const topics = new Map<string, EventTopic & { rank: number }>();
  for (const event of events) {
    const rules = TOPIC_RULES[event.domain] ?? [];
    const rule = ruleFor(event, rules);
    const name = rule?.key ?? (event.subdomain || event.subdomainLabel);
    const key = `${event.domain}.${name}`;
    const topic = topics.get(key);
    if (topic) {
      topic.events.push(event);
    } else {
      topics.set(key, {
        key,
        label: rule?.label ?? event.subdomainLabel,
        events: [event],
        rank: rule ? rules.indexOf(rule) : rules.length,
      });
    }
  }
  return Array.from(topics.values())
    .sort((a, b) => a.rank - b.rank)
    .map(({ key, label, events: rows }) => ({ key, label, events: rows }));
}
